



















































































































































































import { Vue, Component, Prop } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { flatten, padStart, random, sortBy, uniqBy } from 'lodash';
import { LoaderComponent } from 'vue-loading-overlay';
import DashboardUpload from '@/components/dashboard-upload.vue';
import { IUploadedFile } from '@/interfaces/uploaded-file.interface';
import UploadService from '@/services/crm/upload.service';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import dayjs from '@/plugins/dayjs';
import ClientModel from '@/models/crm/client.model';
import { AttachmentTypeEnum } from '@/enums/crm/attachment-type.enum';
import AttachmentService from '@/services/crm/attachment.service';
import AttachmentModel from '@/models/crm/attachment.model';
import DataGridFilter from '@/components/data-grid-filter.vue';
import AttachmentTypeDialog from '@/components/crm/attachment-type-dialog.vue';
import { ArrayHelper } from '@/utils/helpers/array-helper';
import { IKeyValue } from '@/interfaces/key-value.interface';
import CrmAttachmentsViewerDetailsDialog from '@/components/crm/attachments-viewer-details-dialog.vue';
import { IDialogConfig } from '@/interfaces/dialog-config.interface';
import ConfirmationDialog from '@/components/confirmation-dialog.vue';
import { ClientTypeEnum } from '@/enums/client-type.enum';
import { ClientStatusEnum } from '@/enums/crm/client-status.enum';
import ProcessService from '@/services/crm/process.service';

type UploadFilterConfig = {
  keyword: string | undefined;
  type: AttachmentTypeEnum[] | null;
  users: number[];
  flows: number[];
};

@Component({
  components: {
    DashboardUpload,
    DataGridFilter,
    ConfirmationDialog,
    CrmAttachmentsViewerDetailsDialog,
    AttachmentTypeDialog,
  },
})

export default class CrmAttachmentsViewer extends Vue {
  isLoadingAttachments = false;

  showTypeDialog = false;

  get attachmentTypeOptions(): Array<{ code: AttachmentTypeEnum; name: string }> {
    return [
      {
        code: this.attachmentTypeEnum.Frontage,
        name: this.$t(`crm.attachmentsViewer.type.${this.attachmentTypeEnum.Frontage}`).toString(),
      },
      {
        code: this.attachmentTypeEnum.Visitation,
        name: this.$t(`crm.attachmentsViewer.type.${this.attachmentTypeEnum.Visitation}`).toString(),
      },
      {
        code: this.attachmentTypeEnum.Other,
        name: this.$t(`crm.attachmentsViewer.type.${this.attachmentTypeEnum.Other}`).toString(),
      },
    ];
  }

  onSelectAttachmentType(type: string): void {
    this.onOpenUploader(type as AttachmentTypeEnum);
  }

  // eslint-disable-next-line class-methods-use-this
  formatCreationDate(date: Date | string): string {
    if (!date) return '';
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '';
  }

  attachmentTypeEnum = AttachmentTypeEnum;

  @inject(InjectionIdEnum.CrmUploadService)
  protected uploadService!: UploadService;

  @inject(InjectionIdEnum.CrmAttachmentService)
  protected attachmentService!: AttachmentService;

  @inject(InjectionIdEnum.CrmProcessService)
  protected processService!: ProcessService;

  dialogConfig: IKeyValue<IDialogConfig> = {
    confirmation: {
      message: '',
      color: '',
      show: false,
      onChoice: () => {},
    },
  };

  showUploader = false;

  showDetails = false;

  showProspectAttachments = false;

  activeAttachment: AttachmentModel | null = null;

  uploadEndpoint = '';

  uploadHash = CrmAttachmentsViewer.getHash();

  attachmentType: AttachmentTypeEnum = AttachmentTypeEnum.Frontage;

  filters: UploadFilterConfig = {
    keyword: undefined,
    type: null,
    users: [],
    flows: [],
  };

  filterTypeOptions: IKeyValue<string | null>[] = [
    {
      code: AttachmentTypeEnum.Frontage,
      name: `${this.$t(`crm.attachmentsViewer.type.${AttachmentTypeEnum.Frontage}`)}`,
    },
    {
      code: AttachmentTypeEnum.Visitation,
      name: `${this.$t(`crm.attachmentsViewer.type.${AttachmentTypeEnum.Visitation}`)}`,
    },
    {
      code: AttachmentTypeEnum.Order,
      name: `${this.$t(`crm.attachmentsViewer.type.${AttachmentTypeEnum.Order}`)}`,
    },
    {
      code: AttachmentTypeEnum.Other,
      name: `${this.$t(`crm.attachmentsViewer.type.${AttachmentTypeEnum.Other}`)}`,
    },
    {
      code: AttachmentTypeEnum.Process,
      name: `${this.$t(`crm.attachmentsViewer.type.${AttachmentTypeEnum.Process}`)}`,
    },
  ];

  fileTypeIcons = {
    video: 'mdi-video',
    audio: 'mdi-headphones',
    pdf: 'mdi-adobe-acrobat',
    unknown: 'mdi-file',
  };

  attachments: AttachmentModel[] = [];

  attachmentsClient: AttachmentModel[] = [];

  attachmentsProspect: AttachmentModel[] = [];

  @Prop({ required: true })
  client!: ClientModel;

  @Prop({ required: true })
  clientType!: ClientTypeEnum;

  @Prop()
  canRemoveAttachments!: boolean;

  @Prop({ default: false })
  isDialogActive!: boolean;

  async mounted(): Promise<void> {
    this.uploadEndpoint = this.uploadService.getUploadUrl();
    this.isLoadingAttachments = true;
    const loader = this.setBusyLoader();
    try {
      if (this.isDialogActive) {
        await this.loadAttachments(this.clientType);
      }
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      loader.hide();
      this.isLoadingAttachments = false;
    }
  }

  async loadAttachments(clientType: ClientTypeEnum): Promise<void> {
    const loadTasks = [
      this.loadFrontageAttachments(clientType),
      this.loadOtherAttachments(clientType),
      this.loadVisitationAttachments(clientType),
      this.loadProcessAttachments(clientType),
    ];
    if (clientType === ClientTypeEnum.Client) {
      loadTasks.push(this.loadOrderAttachments(clientType));
    }
    const result = await Promise.all(loadTasks);
    this.attachments = sortBy(flatten(result), 'creationDate').reverse();
    if (clientType === ClientTypeEnum.Prospect
    && this.attachmentsProspect.length === 0 && this.attachments.length > 0) {
      this.attachmentsProspect = this.attachments;
    } else if (clientType === ClientTypeEnum.Client
    && this.attachmentsClient.length === 0 && this.attachments.length > 0) {
      this.attachmentsClient = this.attachments;
    }
  }

  async loadFrontageAttachments(clientType: ClientTypeEnum): Promise<AttachmentModel[]> {
    let clientId = clientType === ClientTypeEnum.Prospect ? this.client.codCliente : this.client.cnpjCpf;
    if (this.showProspectAttachments && this.client.prospectId) {
      clientId = this.client.prospectId.toString();
    }

    return (await this.attachmentService.getFrontageAttachments(clientId, clientType)).map((x) => {
      const item = x;
      item.type = AttachmentTypeEnum.Frontage;
      if (clientType !== ClientTypeEnum.Prospect) {
        item.path = this.uploadService.getDownloadUrl(item.path);
      }
      return item;
    });
  }

  async loadVisitationAttachments(clientType: ClientTypeEnum): Promise<AttachmentModel[]> {
    let clientId = clientType === ClientTypeEnum.Prospect ? this.client.codCliente : this.client.cnpjCpf;
    if (this.showProspectAttachments && this.client.prospectId) {
      clientId = this.client.prospectId.toString();
    }

    return (await this.attachmentService.getVisitationAttachments(clientId, clientType)).map((x) => {
      const item = x;
      item.type = AttachmentTypeEnum.Visitation;
      return item;
    });
  }

  async loadOrderAttachments(clientType: ClientTypeEnum): Promise<AttachmentModel[]> {
    const clientId = clientType === ClientTypeEnum.Prospect ? this.client.codCliente : this.client.cnpjCpf;

    return (await this.attachmentService.getOrderAttachments(clientId, clientType)).map((x) => {
      const item = x;
      item.type = AttachmentTypeEnum.Order;
      return item;
    });
  }

  async loadOtherAttachments(clientType: ClientTypeEnum): Promise<AttachmentModel[]> {
    let clientId = clientType === ClientTypeEnum.Prospect ? this.client.codCliente : this.client.cnpjCpf;
    if (this.showProspectAttachments && this.client.prospectId) {
      clientId = this.client.prospectId.toString();
    }

    return (await this.attachmentService.getOtherAttachments(clientId, clientType)).map((x) => {
      const item = x;
      item.type = AttachmentTypeEnum.Other;
      if (clientType !== ClientTypeEnum.Prospect) {
        item.path = this.uploadService.getDownloadUrl(item.path);
      }

      return item;
    });
  }

  async loadProcessAttachments(clientType: ClientTypeEnum): Promise<AttachmentModel[]> {
    let clientId = clientType === ClientTypeEnum.Prospect ? this.client.codCliente : this.client.cnpjCpf;
    if (this.showProspectAttachments && this.client.prospectId) {
      clientId = this.client.prospectId.toString();
    }
    try {
      return (await this.attachmentService.getProcessAttachments(clientId, clientType)).map((x) => {
        const item = x;
        item.type = AttachmentTypeEnum.Process;
        return item;
      });
    } catch (err) {
      this.$notify.error(`${this.$t('crm.attachmentsViewer.error.loadProcess')}`);
      return [];
    }
  }

  onAttachmentClick(attachment: AttachmentModel): void {
    if (attachment.type === AttachmentTypeEnum.Process) {
      this.openProcessInNewTab(attachment);
    } else {
      this.onShowAttachment(attachment);
    }
  }

  openProcessInNewTab(attachment: AttachmentModel): void {
    try {
      const url = this.processService.getNewKanbanUrl(
        attachment.flowId,
        this.clientId,
        this.clientType,
        attachment.queueId,
        attachment.processId,
        true,
        undefined,
        true,
      );
      window.open(url, '_blank');
    } catch {
      this.$notify.error(`${this.$t('crm.attachmentsViewer.error.openProcess')}`);
    }
  }

  onOpenUploader(type: AttachmentTypeEnum): void {
    this.uploadHash = CrmAttachmentsViewer.getHash();
    this.attachmentType = type;
    this.showUploader = true;
  }

  onCloseUploader(): void {
    this.showUploader = false;
  }

  onShowAttachment(attachment: AttachmentModel): void {
    this.activeAttachment = attachment;
    this.showDetails = true;
  }

  get showProspectAttachmentButton(): boolean {
    return this.wasProspectClient && this.clientType === ClientTypeEnum.Client;
  }

  async onOpenProspectAttachments(): Promise<void> {
    this.filters.keyword = '';
    this.filters.type = null;
    this.filters.users = [];
    this.filters.flows = [];
    this.showProspectAttachments = !this.showProspectAttachments;
    if (this.isDialogActive && this.showProspectAttachments) {
      if (this.attachmentsProspect.length === 0) {
        await this.loadAttachments(ClientTypeEnum.Prospect);
      } else {
        this.attachments = this.attachmentsProspect;
      }
    } else {
      this.attachments = this.attachmentsClient;
    }
  }

  async onDeleteAttachment(attachment: AttachmentModel): Promise<void> {
    this.beforeDeleteConfirmation(`${this.$t('global.youAreSureDeleteRecord')}`, async (accept: boolean) => {
      if (accept) {
        const currentList = this.attachments;

        try {
          this.attachments = this.attachments.filter((x) => x.id !== attachment.id);
          if (this.attachmentsClient) {
            this.attachmentsClient = this.attachmentsClient.filter((x) => x.id !== attachment.id);
          }
          if (this.attachmentsProspect) {
            this.attachmentsProspect = this.attachmentsProspect.filter((x) => x.id !== attachment.id);
          }

          if (attachment.type === AttachmentTypeEnum.Frontage) {
            await this.attachmentService.deleteFrontage(attachment.id, this.clientType);
          } else if (attachment.type === AttachmentTypeEnum.Visitation) {
            await this.attachmentService.deleteVisitation(attachment.id, this.clientType);
          } else if (attachment.type === AttachmentTypeEnum.Order) {
            await this.attachmentService.deleteOrder(attachment.id);
          } else {
            await this.attachmentService.deleteOther(attachment.id, this.clientType);
          }
        } catch (err) {
          this.attachments = currentList;

          this.$notify.error(err && (err as Error).message);
        }
      }
    });
  }

  async onUploadFiles(files: IUploadedFile[]): Promise<void> {
    const attachments = files.map((upload) => {
      const model = new AttachmentModel();

      model.clientId = this.clientId;
      model.path = upload.path;
      model.description = upload.description || '';
      model.name = upload.filename;

      return model;
    });

    try {
      let result: AttachmentModel[];
      if (this.attachmentType === AttachmentTypeEnum.Frontage) {
        result = (await this.attachmentService.saveFrontage(attachments, this.clientType)).map((x) => {
          const item = x;
          item.type = AttachmentTypeEnum.Frontage;
          item.path = this.uploadService.getDownloadUrl(item.path);
          return item;
        });
      } else if (this.attachmentType === AttachmentTypeEnum.Visitation) {
        result = (await this.attachmentService.saveVisitation(attachments, this.clientType)).map((x) => {
          const item = x;
          item.type = AttachmentTypeEnum.Visitation;
          item.path = this.uploadService.getDownloadUrl(item.path);
          return item;
        });
      } else {
        result = (await this.attachmentService.saveOther(attachments, this.clientType)).map((x) => {
          const item = x;
          item.type = AttachmentTypeEnum.Other;
          item.path = this.uploadService.getDownloadUrl(item.path);
          return item;
        });
      }

      this.attachments = [...this.attachments, ...result];
      if (this.clientType === ClientTypeEnum.Client) {
        this.attachmentsClient = this.attachments;
      }
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    }
  }

  beforeDeleteConfirmation(message: string, onChoice: CallableFunction): void {
    this.dialogConfig.confirmation.message = message;
    this.dialogConfig.confirmation.color = 'red';
    this.dialogConfig.confirmation.onChoice = onChoice;
    this.dialogConfig.confirmation.show = true;
  }

  get filterTypeOptionsByClientType(): IKeyValue<string | null>[] {
    const availableTypes = Array.from(new Set(this.attachments.map((a) => a.type))) as AttachmentTypeEnum[];
    return this.filterTypeOptions.filter(
      (opt) => availableTypes.includes(opt.code as AttachmentTypeEnum),
    );
  }

  get filterUserOptions(): { id: number; name: string }[] {
    return uniqBy(
      this.attachments
        .filter((a) => a.creationUser && a.creationUserId)
        .map((a) => ({ id: a.creationUserId, name: a.creationUser })),
      'id',
    );
  }

  get filterFlowOptions(): { id: number; name: string }[] {
    return uniqBy(
      this.attachments
        .filter((a) => a.type === AttachmentTypeEnum.Process && a.flowId && a.flowName)
        .map((a) => ({ id: a.flowId, name: a.flowName })),
      'id',
    );
  }

  get clientId(): string {
    return this.clientType === ClientTypeEnum.Prospect ? this.client.codCliente : this.client.cnpjCpf;
  }

  get filteredAttachments(): AttachmentModel[] {
    let filteredItems = this.attachments;

    if (this.filters.keyword) {
      const columnsToSearch = ['name', 'description'];
      filteredItems = ArrayHelper.filterByKeyword(filteredItems, columnsToSearch, this.filters.keyword);
    }

    if (this.filters.type && this.filters.type.length > 0) {
      filteredItems = filteredItems.filter((item) => (this.filters.type as AttachmentTypeEnum[]).includes(item.type));
    }

    if (this.filters.users && this.filters.users.length > 0) {
      filteredItems = filteredItems.filter((item) => this.filters.users.includes(item.creationUserId));
    }

    if (this.filters.flows && this.filters.flows.length > 0) {
      filteredItems = filteredItems.filter((item) => this.filters.flows.includes(item.flowId));
    }

    return filteredItems;
  }

  get fileTypes(): IKeyValue {
    const mappedTypes: IKeyValue = {};

    this.filteredAttachments.forEach((file) => {
      const filename = file.name || file.path;

      const regexp = new RegExp(/\.[0-9a-z]{1,5}$/i);
      const regexpMatch = filename.match(regexp) || [];

      const typeMatrix = {
        '.jpg': 'image',
        '.jpeg': 'image',
        '.gif': 'image',
        '.png': 'image',
        '.mp4': 'video',
        '.pdf': 'pdf',
        '.mp3': 'audio',
      };

      const ext = regexpMatch[0] || '';
      mappedTypes[`${file.type}_${file.id}`] = typeMatrix[ext] || 'unknown';
    });

    return mappedTypes;
  }

  get uploadTitle(): string {
    return `${this.$t('crm.attachmentsViewer.dialog.dashboardUpload.title', {
      area: this.$t(`crm.attachmentsViewer.type.${this.attachmentType}`),
    })}`;
  }

  get activeFilters(): number {
    let active = 0;
    const filterKeys = Object.keys(this.filters);

    filterKeys.forEach((key) => {
      switch (key) {
        default:
          if (this.filters[key]) {
            active += 1;
          }
      }
    });

    return active;
  }

  get isConvertedProspect(): boolean {
    return this.client.type === ClientTypeEnum.Prospect && this.client.situacao === ClientStatusEnum.Converted;
  }

  get wasProspectClient(): boolean {
    return !!this.client.prospectId;
  }

  static getHash(): string {
    return `${dayjs().format('HHmmssDD')}${padStart(random(1, 999).toString(), 3, '0')}`;
  }

  private setBusyLoader(): LoaderComponent {
    return this.$loading.show({
      container: this.$refs.attachmentsViewerContainer,
      canCancel: false,
    });
  }
}
